import {HistoryEntry} from './history-types';
import {FetchNextPageCompletedAction, SearchCompletedAction, SearchPageAction} from './search-types';
import {
    ProdDocument,
    ProductDocument,
    SearchExportFormat,
    SearchRequest,
    SearchResult,
    SortType,
    TabType,
} from '../search-types';
import {DeepReadonly} from 'utility-types';
import {Dispatch, GetState, State, TempThunkAction} from '../../app-types';
import {ThunkAction} from 'redux-thunk';
import {getActualQueryString, getSearchRequest, isBlacklistedForAutoCorrection, mapDocument} from '../search.utils';
import {doClosePointInTime, doFetchFacets, doFetchNextDocuments, doSearchRequest} from '../../services/search-service';
import {getErrorMessage, scrollToTop} from '../../utils/utils';
import {SEARCH_RESULTS_LIST_CLASS} from '../search-results/search-result-list/search-results-list';
import {searchPageAttributesMap, SearchPageType} from '../search-config';
import {buildEncodedUrlPath} from '../url-path-builder';
import {ClickableElement} from '../search-results/search-result-details/content/clickable-section-list/clickableSectionList';
import {
    DocumentHighlights,
    PreviewDocument
} from '../search-results/search-preview/doc-preview-section/docPreviewRow-types';
import {PdfHighlight, SpanHighlight} from '../../common/pdf-viewer/PdfViewer.types';
import {hasPdfCoordinates} from '../../common/pdf-viewer/spanMath';
import {
    fetchHighlightedDocument,
    fetchPdfDocument,
    fetchPlainDocument,
} from '../search-results/search-preview/doc-preview-section/docPreviewRow-service';
import {NavigateFunction} from 'react-router-dom';
import {QUERY_FIELDS_ALL, QUERY_FIELDS_SEPARATOR} from '../const';
import {DynamicSize} from '../../common/dragable-divider/dragableDivider';
import {DefaultRootState} from 'react-redux';
import {store} from '../../store';
import {showInfo} from '../../message-box/message-box-actions';
import {t} from 'i18next';
import {HibuFacetFilter, HibuFacetGroup} from '../../facets/facet.types';
import {updateAllFilters} from '../../facets/duck/filter.actions';

export const searchHistoryLoadedAction = (
    searchPageType: SearchPageType,
    entries: HistoryEntry[],
): SearchPageAction => {
    return {
        type: 'SEARCH_HISTORY_LOADED',
        searchPageType,
        entries,
    };
};

export const searchHistoryClearedAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        type: 'SEARCH_HISTORY_LOADED',
        searchPageType,
        entries: [],
    };
};

export const searchScrollButtonVisibilityChanged = (
    searchPageType: SearchPageType,
    showScrollButton: boolean,
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_SCROLL_BUTTON_VISIBILITY_CHANGED',
        showScrollButton: showScrollButton,
    };
};

export const searchResultSetExpandedDocumentAction = (
    searchPageType: SearchPageType,
    doc: DeepReadonly<ProdDocument> | undefined,
): SearchPageAction => ({
    searchPageType: searchPageType,
    type: 'SEARCH_RESULT_SET_EXPANDED_DOCUMENT',
    doc: doc,
});

// SEARCH

export const resetSearchStateParts = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'RESET_SEARCH_STATE_PARTS',
    };
};

export const changeInDocumentQueryAction = (inDocumentQuery: string | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SEARCH_CHANGE_IN_DOCUMENT_QUERY',
        inDocumentQuery: inDocumentQuery,
    };
};

export const searchSourceSwitchedAction = (searchPageType: SearchPageType, name: string): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_SOURCE_SWITCHED',
        name: name,
    };
};

export const exportRequestedAction = (
    searchPageType: SearchPageType,
    exportFormat: SearchExportFormat,
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'EXPORT_REQUESTED',
        reportFormat: exportFormat,
    };
};

export const fetchNextPageStartedAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'FETCH_NEXT_PAGE_STARTED',
    };
};

export const sortChangeAction = (searchPageType: SearchPageType, eventKey: SortType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'RESULT_SORT_CHANGED',
        sortType: eventKey,
    };
};

export const startSearchAction = (searchPageType: SearchPageType, query = ''): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_STARTED',
        query: query,
    };
};

export const doneSearchingAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'DONE_SEARCHING_ACTION',
    };
};

export const startSearchChatAction = (
    searchPageType: SearchPageType,
    isChatOpen: boolean,
    currentState: string,
): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_CHAT_TRIGGERED',
        isChatOpen: isChatOpen,
        currentState: currentState,
    };
};

export const addBookmarkAction = (searchPageType: SearchPageType, doc: ProdDocument): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_ADD_BOOKMARK',
        doc: doc,
    };
};

export const removeBookmarkAction = (searchPageType: SearchPageType, doc: ProdDocument): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_REMOVE_BOOKMARK',
        doc: doc,
    };
};

export function setSearchQueryFieldsAction(
    searchPageType: SearchPageType,
    containerQueries: string[],
): SearchPageAction {
    return {
        type: 'SET_SEARCH_QUERY_FIELDS',
        searchPageType: searchPageType,
        queryFields: containerQueries,
    };
}

export const searchCompletedAction = (
    searchResult: Partial<SearchResult>,
    searchPageType: SearchPageType,
): SearchCompletedAction => {
    return {
        searchPageType: searchPageType,
        type: 'SEARCH_COMPLETED',
        searchResult: searchResult,
    };
};

export const fetchNextPageCompletedAction = (
    docs: ProdDocument[],
    pointInTime?: string,
): FetchNextPageCompletedAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'FETCH_NEXT_PAGE_COMPLETED',
        docs: docs,
        pointInTime: pointInTime,
    };
};

export const clearPointInTimeAction = (searchPageType: SearchPageType): SearchPageAction => {
    return {
        searchPageType: searchPageType,
        type: 'CLEAR_POINT_IN_TIME',
    };
};

export const expandDocumentDetailsAction = (expanded: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'EXPAND_DOCUMENT_DETAILS',
        expanded: expanded,
    };
};

export const setPreviewDocumentAction = (previewDocument: PreviewDocument | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'PREVIEW_DOCUMENT',
        previewDocument: previewDocument,
    };
};

export const setDocumentHighlightsAction = (
    documentHighlights: DocumentHighlights | undefined,
): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_DOCUMENT_HIGHLIGHTS',
        documentHighlights: documentHighlights,
    };
};

export const showAnnotations = (showAnnotations: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SHOW_ANNOTATIONS',
        showAnnotations: showAnnotations,
    };
};

export const setDocumentQueryHighlights = (queryHighlights: SpanHighlight[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_DOCUMENT_QUERY_HIGHLIGHTS',
        documentAnnotations: queryHighlights,
    };
};

export const setDocumentSelectedTags = (selectedTags: PdfHighlight[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_DOCUMENT_SELECTED_TAGS',
        documentSelectedTags: selectedTags,
    };
};

export const switchResultDetailsTabAction = (resultDetailsTab: TabType): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SWITCH_RESULT_DETAILS_TAB',
        openResultTab: resultDetailsTab,
    };
};

export const changeResultDetailsTabSizeAction = (size: DynamicSize): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CHANGE_RESULT_DETAILS_TAB_SIZE',
        size: size,
    };
};

export const setQueryFieldsAction = (queryFieldsString: string | null): SearchPageAction => {
    let queryFields: string[] = [];
    const state = store.getState() as DefaultRootState;
    const fieldSpecificSearch = state.settings.fieldSpecificSearch;
    if (queryFieldsString !== null) {
        queryFields = queryFieldsString.split(QUERY_FIELDS_SEPARATOR);
        if (!fieldSpecificSearch) {
            // enable field specific search in user settings
            store.dispatch({type: 'FIELD_SPECIFIC_SEARCH_SET', fieldSpecificSearch: true});
            // show info message that field specific search has been enabled automatically
            store.dispatch(
                showInfo(
                    SearchPageType.BASIC,
                    t('search.info.fieldSpecificSearchEnabled.message'),
                    t('search.info.fieldSpecificSearchEnabled.title'),
                    4000,
                ),
            );
        }
    } else if (fieldSpecificSearch) {
        queryFields = [QUERY_FIELDS_ALL]; // show all if no URL parameter present, but field specific search enabled
    }
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_SEARCH_QUERY_FIELDS',
        queryFields: queryFields,
    };
};

export const setCurrentPageNumberAction = (newPageNumber: number | undefined): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_DOCUMENT_PAGE_CHANGED',
        newPageNumber: newPageNumber,
    };
};

export const setCurrentPageRotationAction = (newRotation: number): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CURRENT_PAGE_ROTATED',
        rotation: newRotation,
    };
};

export const setDocumentAllPagesRotation = (rotations: number[]): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'CURRENT_DOCUMENT_ROTATIONS',
        rotations: rotations,
    };
};

export const setScrollTargetAction = (scrollTarget: ClickableElement): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SCROLL_TARGET_CHANGED',
        newPageNumber: scrollTarget.pageNumber,
        scrollTarget: scrollTarget,
    };
};

export const resetTransformAction = (resetTransform: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'RESET_TRANSFORM',
        resetTransform: resetTransform,
    };
};

/**
 * @param navigate `true` only for a user-initiated jump. It bumps `navigationRequest` so re-selecting
 * the highlight already in state still scrolls; load-time and clearing dispatches leave it `false`.
 */
export const setHighlightIdAction = (highlightId: string | undefined, navigate = false): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_HIGHLIGHT_ID',
        highlightId: highlightId,
        navigate: navigate,
    };
};

export const setSelectedDocumentNumberOfPages = (numPages: number): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SELECTED_DOCUMENT_CHANGED',
        numberOfPages: numPages,
    };
};

export const setUseAutoCorrection = (useAutoCorrection: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'UPDATE_AUTO_CORRECTION',
        useAutoCorrection: useAutoCorrection,
    };
};

export const setExpandedSpotlightGroups = (spotlightGroup: string, expanded: boolean): SearchPageAction => {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'UPDATE_EXPANDED_SPOTLIGHT_GROUPS',
        spotlightGroup: spotlightGroup,
        expanded: expanded,
    };
};

export function setSortSettingsAction(sortTypes: SortType[]): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_SORT_SETTINGS',
        sortTypes: sortTypes,
    };
}

export function setIsLoadingDocumentAction(isLoading: boolean): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'IS_LOADING_DOCUMENT',
        isLoading: isLoading,
    };
}

function setInDocumentSearchPendingAction(isPending: boolean): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_IN_DOCUMENT_SEARCH_PENDING',
        isPending: isPending,
    };
}

function setPreviewHeldForHitsAction(isHeld: boolean): SearchPageAction {
    return {
        searchPageType: SearchPageType.BASIC,
        type: 'SET_PREVIEW_HELD_FOR_HITS',
        isHeld: isHeld,
    };
}

let highlightRequestSeq = 0;

/**
 * Invalidates every highlight request already in flight: their responses stop matching and are
 * dropped, and the in-document search spinner and any held preview go away. Every path that clears the
 * slot must call this, or a late response undoes the clear.
 */
const invalidateHighlightRequests = (dispatch: Dispatch) => {
    dispatch(setInDocumentSearchPendingAction(false));
    dispatch(setPreviewHeldForHitsAction(false));
    return ++highlightRequestSeq;
};

export const doFetchPreviewFile = (selectedDocument: ProductDocument | undefined) => {
    return (dispatch: Dispatch): void => {
        if (selectedDocument) {
            dispatch(setIsLoadingDocumentAction(true));
            fetchPdfDocument(selectedDocument.metadata.previewLink_string)
                .then((fetchDocument) => dispatch(setPreviewDocumentAction(fetchDocument)))
                .finally(() => dispatch(setIsLoadingDocumentAction(false)));
        }
    };
};

export const doFetchDocumentHighlights = (
    selectedDocument: ProductDocument | undefined,
    resultFor: string,
    query: string,
    settingsContainerName: string,
    {showProgress = false, holdPreview = false}: { showProgress?: boolean; holdPreview?: boolean } = {},
) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        if (!selectedDocument) {
            return;
        }
        const documentId = selectedDocument.id;
        const term = resultFor || query;
        if (!term) {
            invalidateHighlightRequests(dispatch);
            dispatch(setDocumentHighlightsAction(undefined));
            dispatch(setHighlightIdAction(undefined));
            return;
        }
        const requestSeq = invalidateHighlightRequests(dispatch);
        if (showProgress) {
            dispatch(setInDocumentSearchPendingAction(true));
        }
        if (holdPreview) {
            dispatch(setPreviewHeldForHitsAction(true));
        }
        const isNewest = () => requestSeq === highlightRequestSeq;
        // A response only counts if it is still the newest request for the document on screen.
        const isCurrent = () =>
            isNewest() && getState().search[SearchPageType.BASIC].expandedProdDocument?.id === documentId;
        // Called after the results are written, so a held preview shows up with its hits already in place.
        const endProgressIfNewest = () => {
            if (isNewest()) {
                dispatch(setInDocumentSearchPendingAction(false));
                dispatch(setPreviewHeldForHitsAction(false));
            }
        };

        fetchHighlightedDocument(
            documentId,
            term,
            settingsContainerName,
            selectedDocument.metadata.previewLink_string)
            .then((payload) => {
                // Skipped when aborted, or overtaken by a newer request or a different document.
                if (payload !== undefined && isCurrent()) {
                    // Open on the first hit's page instead of turning to it after page 1 has drawn. The first hit
                    // is the first span with coordinates, the same one buildQueryHighlights puts first.
                    const firstHitPage = payload.querySpans.find(hasPdfCoordinates)?.pageNumber;
                    if (holdPreview && firstHitPage !== undefined) {
                        dispatch(setCurrentPageNumberAction(firstHitPage));
                    }
                    dispatch(setDocumentHighlightsAction(payload));
                }
                endProgressIfNewest();
            })
            .catch((error) => {
                console.log('Error: ', getErrorMessage(error));
                if (isCurrent()) {
                    dispatch(setDocumentHighlightsAction({querySpans: [], error}));
                    dispatch(setHighlightIdAction(undefined));
                }
                endProgressIfNewest();
            });
    };
};

export const openNewDocument = (doc: ProdDocument | undefined) => {
    return (dispatch: Dispatch): void => {
        invalidateHighlightRequests(dispatch);
        dispatch(setPreviewDocumentAction(undefined));
        dispatch(setDocumentHighlightsAction(undefined));
        dispatch(setHighlightIdAction(undefined));
        dispatch(setCurrentPageNumberAction(1));
        dispatch(setDocumentSelectedTags([]));
        dispatch(fetchDocumentDetails(doc));
    };
};

export const fetchDocumentDetails = (sourceDoc: ProdDocument | undefined) => {
    return (dispatch: Dispatch, getState: GetState): void => {
        const searchPageType = SearchPageType.BASIC;
        const settingsContainerName = getState().settings.containerName;
        if (sourceDoc) {
            dispatch(setIsLoadingDocumentAction(true));
            fetchPlainDocument(searchPageType, sourceDoc.id, settingsContainerName).then((doc) => {
                if (doc) {
                    const productDocument = mapDocument(doc, [], sourceDoc.duplicates);
                    dispatch(searchResultSetExpandedDocumentAction(searchPageType, productDocument));
                    dispatch(setIsLoadingDocumentAction(false));
                }
            });
        }
    };
};

export const fetchNextPage = (searchPageType: SearchPageType, useAutoCorrection: boolean): TempThunkAction => {
    return (dispatch: Dispatch, getState: GetState) => {
        const request: SearchRequest = getSearchRequest(getState, undefined, useAutoCorrection);
        dispatch(fetchNextPageStartedAction(searchPageType));
        dispatch(setUseAutoCorrection(useAutoCorrection));
        return dispatch(doFetchNextDocuments(request));
    };
};

export const sortResults = (searchPageType: SearchPageType, eventKey: SortType, navigate: NavigateFunction) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const queryString: string = getActualQueryString(getState().search[searchPageType]);
        dispatch(sortChangeAction(searchPageType, eventKey));
        return dispatch(search(navigate, queryString, false, false));
    };
};

export const fetchFacets = (query?: string, isUsingAutoCorrection = true) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const searchPageType: SearchPageType = SearchPageType.BASIC;
        const request: SearchRequest = {
            ...getSearchRequest(getState, query, isUsingAutoCorrection),
            pointInTime: undefined,
        };
        return dispatch(doFetchFacets(searchPageType, request));
    };
};

export const search = (
    navigate: NavigateFunction,
    query?: string,
    isSubmittingNewQuery = true,
    isUsingAutoCorrection = true,
): TempThunkAction => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const searchPageType: SearchPageType = SearchPageType.BASIC;
        query = query ?? getState().search[searchPageType].query;
        const pitId = getState().search[searchPageType].pointInTime;
        if (pitId) {
            dispatch(doClosePointInTime(pitId));
        }
        scrollToTop(SEARCH_RESULTS_LIST_CLASS);
        dispatch(startSearchAction(searchPageType, query));

        const request: SearchRequest = getSearchRequest(getState, query, isUsingAutoCorrection);
        const useAutoCorrection = shouldUseAutoCorrection(isUsingAutoCorrection, query);
        dispatch(setUseAutoCorrection(useAutoCorrection));

        dispatch(resetResultDetails());
        const replaceUrl = (searchExpression: string) => {
            changeUrl(getState, searchPageType, navigate, searchExpression);
        };
        return dispatch(doSearchRequest(request, isSubmittingNewQuery, true, replaceUrl));
    };
};

export const searchSavedSearch = (request: SearchRequest, navigate: NavigateFunction) => {
    return (dispatch: Dispatch, getState: GetState): any => {
        const searchPageType: SearchPageType = SearchPageType.BASIC;
        const pitId = getState().search[searchPageType].pointInTime;
        if (pitId) {
            dispatch(doClosePointInTime(pitId));
        }
        // scroll to top before executing the task. Prevents the infinite scroll list from loading a huge amount of items
        scrollToTop(SEARCH_RESULTS_LIST_CLASS);
        dispatch(startSearchAction(searchPageType, request.query));
        dispatch(updateAllFilters(request.filters));
        dispatch(resetResultDetails());
        const replaceUrl = (searchExpression: string) => {
            changeUrl(getState, searchPageType, navigate, searchExpression);
        };
        return dispatch(doSearchRequest(request, true, true, replaceUrl));
    };
};

function shouldUseAutoCorrection(isUsingAutoCorrection: boolean, query: string) {
    return isUsingAutoCorrection && !isBlacklistedForAutoCorrection(query);
}

export const resetResultDetails = (): ThunkAction<void, State, unknown, SearchPageAction> => {
    return (dispatch: Dispatch) => {
        invalidateHighlightRequests(dispatch);
        dispatch(setPreviewDocumentAction(undefined));
        dispatch(setDocumentHighlightsAction(undefined));
        dispatch(setHighlightIdAction(undefined));
        dispatch(searchResultSetExpandedDocumentAction(SearchPageType.BASIC, undefined));
        dispatch(changeInDocumentQueryAction(undefined));
        dispatch(setCurrentPageNumberAction(1));
    };
};

export const changeUrl = (
    getState: GetState,
    searchPageType: SearchPageType,
    navigate: NavigateFunction,
    newSearchExpression = '',
): void => {
    const replaceSearchExpression: boolean = newSearchExpression !== '';
    const searchExpression: string = replaceSearchExpression
        ? newSearchExpression
        : getState().search[searchPageType].query;
    const sortField = getState().search[searchPageType].currentSortType.field;
    const facets: HibuFacetGroup[] = getState().filter.facets;
    const selectedFilters: HibuFacetFilter = getState().filter.selectedFilters;

    const openTab = getState().search[searchPageType].resultDetailsTab.openResultTab;
    const showFieldSpecificSearch = isShowFieldSpecificSearch(getState());
    const queryFields = getState().search[searchPageType].queryFields;
    const filterPersisted = getState().filter.filterPersisted;

    const encodedUrlPath: string = buildEncodedUrlPath(
        searchPageAttributesMap[searchPageType].route,
        searchExpression,
        sortField,
        facets,
        selectedFilters,
        openTab,
        showFieldSpecificSearch,
        queryFields,
        filterPersisted,
    );
    replaceSearchExpression ? navigate(encodedUrlPath, {replace: true}) : navigate(encodedUrlPath);
};

export const isShowFieldSpecificSearch = (state: DefaultRootState): boolean =>
    state.config.uiConfig.settings.toggles.showFieldSpecificSearch;
